import React, { useState } from 'react';
import { AlertCircle, Barcode, ClipboardList, History, Info, Loader2, MapPin, RefreshCw } from 'lucide-react';
import { ItemCardTab, ItemCardTabBar, ItemCardTabId, useItemCardTab } from './ItemCardTabs';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { LabelPrintModal, type LabelPrintItem } from '../../../common/LabelPrintModal';
import { ItemBarcodesSection } from './sections/ItemBarcodesSection';
import { useItemOverview } from './useItemOverview';
import { ItemCardHeader } from './sections/ItemCardHeader';
import { ItemLocationsSection } from './sections/ItemLocationsSection';
import { ItemOpenTasksSection } from './sections/ItemOpenTasksSection';
import { ItemTransactionsSection } from './sections/ItemTransactionsSection';
import { ItemBasicInfoSection } from './sections/ItemBasicInfoSection';
import { ItemAvailabilitySection } from './sections/ItemAvailabilitySection';
import { ItemOrderLinesSection, ItemPurchaseLinesSection } from './sections/ItemOrdersSection';
import { ItemConsumptionSection } from './sections/ItemConsumptionSection';
import { ItemAuditLine, ItemFreeTextPanel, ItemStockControlSection } from './sections/ItemStockControlSection';
import { SectionCard } from './sections/SectionCard';

interface ItemCardViewProps {
  itemNo: string;
  /** Operatør: aktivt lager (TW 36386708). Lagersjef: null = alle lagre. */
  warehouseId: string | null;
  /** Lagersjef/administrator (OperatorGroupId 2/3) — styrer kun «Rediger vare». */
  isManager: boolean;
  onBack: () => void;
  /** Tekst på tilbake-knappen når varekortet åpnes fra en annen modul (standard: «Nytt søk»). */
  backLabel?: string;
}

/**
 * Varekortet for én vare (Plan-Varekort-v2.md). Header (bilde, KPI, knapper) øverst, resten i faner:
 * Oversikt (hvor er den + åpne oppgaver), Ordre og innkjøp, Historikk (bevegelser + forbruk) og
 * Varedata (grunndata, pris, lagerstyring, strekkoder, fritekst, endringslogg). Samme innhold for alle
 * roller siden 05.11.2026 (erstatter Vareadministrasjon) — kun «Rediger vare» er forbeholdt lagersjef.
 */
export const ItemCardView: React.FC<ItemCardViewProps> = ({ itemNo, warehouseId, isManager, onBack, backLabel: backLabelProp }) => {
  const { t } = useTranslation();
  const { overview, loading, error, notFound, reload } = useItemOverview(itemNo, warehouseId);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [requestedTab, setActiveTab] = useItemCardTab();

  const backLabel = backLabelProp ?? t('warehouse.modules.itemCard.backToSearch', {}, 'Nytt søk');

  if (loading && !overview) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label={backLabel} />
        <div className="bg-white rounded-lg shadow-sm flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (notFound || error || !overview) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label={backLabel} />
        <div className="bg-white rounded-lg shadow-sm p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm flex-1">
            {notFound
              ? t('warehouse.modules.itemCard.notFound', { '0': itemNo }, `Fant ikke varen ${itemNo}`)
              : t('warehouse.modules.itemCard.loadError', {}, 'Kunne ikke laste varekortet')}
          </p>
          {!notFound && (
            <button
              type="button"
              onClick={() => void reload()}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <RefreshCw className="h-4 w-4" />
              {t('warehouse.modules.itemCard.retry', {}, 'Prøv igjen')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const { item } = overview;
  // Serverens svar er fasit: seksjonene (ordre, innkjøp, forbruk, pris) vises når API-et leverte dem —
  // i dag for alle roller. Rolle styrer bare «Rediger vare» (isManager).
  const isManagerView = overview.includesManagerData;
  const { openTasks } = overview;
  const openTaskCount = openTasks.picking.length + openTasks.putAwayReplenishment.length
    + openTasks.awaitingPutAway.length + openTasks.pendingTransactions.length;

  const tabs: ItemCardTab[] = [
    { id: 'overview', label: t('warehouse.modules.itemCard.tabs.overview', {}, 'Oversikt'), icon: <MapPin className="h-4 w-4" />, badge: openTaskCount },
    ...(isManagerView
      ? [{ id: 'orders' as const, label: t('warehouse.modules.itemCard.tabs.orders', {}, 'Ordre og innkjøp'), icon: <ClipboardList className="h-4 w-4" />, badge: overview.orderLines.length + overview.purchaseLines.length }]
      : []),
    { id: 'history', label: t('warehouse.modules.itemCard.tabs.history', {}, 'Historikk'), icon: <History className="h-4 w-4" />, badge: overview.recentTransactions.length },
    { id: 'itemData', label: t('warehouse.modules.itemCard.tabs.itemData', {}, 'Varedata'), icon: <Info className="h-4 w-4" /> },
  ];
  // Fane fra URL som ikke er tilgjengelig for rollen (f.eks. «orders» for operatør) → Oversikt
  const activeTab: ItemCardTabId = tabs.some(tab => tab.id === requestedTab) ? requestedTab : 'overview';

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between gap-3">
        <BackButton onClick={onBack} label={backLabel} />
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          title={t('warehouse.modules.itemCard.refresh', {}, 'Oppdater')}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{t('warehouse.modules.itemCard.refresh', {}, 'Oppdater')}</span>
        </button>
      </div>

      <ItemCardHeader
        item={item}
        totals={overview.totals}
        onPrintLabel={() => setShowPrintModal(true)}
        editHref={isManager
          ? `/?module=warehouse-maintenance&sub=items&itemNo=${encodeURIComponent(item.itemNo)}`
          : undefined}
      />

      <ItemCardTabBar tabs={tabs} active={activeTab} onSelect={setActiveTab} />

      {activeTab === 'overview' && (
        <>
          <ItemLocationsSection
            bins={overview.bins}
            itemType={item.itemType}
            advancedConfig={overview.advancedConfig}
            stdBinCode={item.stdBinCode}
            stdLocationName={item.stdLocationName}
          />
          <ItemOpenTasksSection tasks={overview.openTasks} />
        </>
      )}

      {activeTab === 'orders' && isManagerView && (
        <>
          <ItemAvailabilitySection points={overview.availability} />
          <ItemOrderLinesSection lines={overview.orderLines} />
          <ItemPurchaseLinesSection lines={overview.purchaseLines} />
        </>
      )}

      {activeTab === 'history' && (
        <>
          {isManagerView && overview.consumption && <ItemConsumptionSection consumption={overview.consumption} />}
          <ItemTransactionsSection transactions={overview.recentTransactions} itemType={item.itemType} />
        </>
      )}

      {activeTab === 'itemData' && (
        <>
          {/* Produktark: varen til venstre, lageret til høyre (én kolonne på mobil) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 items-start">
            <div className="space-y-3 sm:space-y-4">
              <ItemBasicInfoSection item={item} showPrice={isManagerView} />
              {isManagerView && <ItemFreeTextPanel item={item} />}
            </div>
            <div className="space-y-3 sm:space-y-4">
              {/* Lagerstyring/plassering/konfigurasjon er nyttig også for operatør (ingen pris her) */}
              <ItemStockControlSection item={item} config={overview.advancedConfig} />
              <SectionCard icon={<Barcode className="h-4 w-4" />} title={t('warehouse.modules.itemCard.barcodes.title', {}, 'Alternative strekkoder')}>
                <ItemBarcodesSection companyNo={item.companyNo} itemNo={item.itemNo} itemUnitCode={item.itemUnitCode} hideHeading />
              </SectionCard>
            </div>
          </div>
          {isManagerView && <ItemAuditLine item={item} />}
        </>
      )}

      <LabelPrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        printType="item"
        items={[{
          id: item.itemNo,
          displayName: `${item.itemNo} - ${item.description1 || ''}`.trim(),
          itemNo: item.itemNo,
          itemDescription: item.description1 || undefined,
        } as LabelPrintItem]}
      />
    </div>
  );
};
